import React, { useState, useEffect } from 'react'

/* ───────────────────────────────────────────────────────────────
 *  LINK CARD CONFIG — tune the look here
 * ─────────────────────────────────────────────────────────────── */
const CONFIG = {
  cardMaxWidth: '480px',
  cardBorderRadius: '14px',
  cardMarginTop: '8px',
  cardBackground: 'var(--color-bg-card-solid)',
  cardBorder: 'var(--border-thin)',

  previewHeight: '180px',
  previewObjectFit: 'cover',

  placeholderHeight: '180px',
  placeholderBg: 'rgba(40,40,44,0.9)',

  iconSize: 35,
  iconColor: 'rgba(255,255,255,0.35)',

  textPadding: '8px 12px 10px',

  urlFontSize: 'var(--text-s)',
  urlColor: 'var(--color-text-secondary)',
  urlMarginBottom: '3px',

  titleFontSize: '18px',
  titleColor: 'var(--color-text-link)',
  titleFontWeight: 'var(--weight-medium)',
  titleLineHeight: 'var(--leading-snug)',
  titleMaxLines: 2,

  loadingFontSize: 'var(--text-sm)',
  loadingColor: 'rgba(0,122,255,0.5)',

  urlPathMaxLength: 28,
  urlFallbackLength: 40,
  titleMaxLength: 80,

  // How long to wait for /api/og before giving up and showing the plain
  // card. Without this, a slow request on weak wifi leaves the card stuck
  // on "Loading preview…" forever.
  fetchTimeoutMs: 8000,
}

/* ───────────────────────────────────────────────────────────────
 *  PERF — two-layer caching
 *
 *  1. THIS in-memory cache survives across all <LinkPreviewCard>
 *     instances for the browser session. Re-mounting the feed or
 *     switching tabs doesn't refetch anything you've already loaded.
 *
 *  2. Vercel EDGE cache on /api/og survives across users and
 *     sessions for ~24 hours. Even on first visit, popular links
 *     come back from the global CDN in ~50ms.
 *
 *  inflight: dedupes simultaneous fetches for the same URL — if
 *  five cards mount at once with the same link, only one fires;
 *  the others piggyback on its result.
 * ─────────────────────────────────────────────────────────────── */
const previewCache = new Map()    // url -> { title, image }
const inflight = new Map()        // url -> Promise<{ title, image } | null>

const SFLinkIcon = ({ size = 28, color = 'rgba(255,255,255,0.35)' }) => (
  <svg width={size} height={size} viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M6.4043 9.14648C5.72461 9.01758 5.26172 8.75977 4.91602 8.41992C3.46875 6.9668 3.47461 4.92773 4.91016 3.49219L6.94922 1.44727C8.38477 0.0117188 10.4238 0 11.877 1.45898C13.3301 2.91211 13.3242 4.95117 11.8828 6.38672L10.2012 8.0625C10.3008 7.61133 10.2422 7.08984 10.0605 6.66211L11.0625 5.6543C12.0527 4.66992 12.0586 3.27539 11.0566 2.27344C10.0664 1.2832 8.67188 1.2832 7.67578 2.27344L5.73047 4.21875C4.74609 5.20312 4.74609 6.60938 5.73633 7.59961C6.09961 7.96289 6.5918 8.18555 7.29492 8.25L6.4043 9.14648ZM6.92578 4.20703C7.60547 4.33594 8.06836 4.59375 8.4082 4.93945C9.86133 6.38672 9.85547 8.42578 8.41406 9.86719L6.38086 11.9062C4.94531 13.3418 2.90625 13.3535 1.45312 11.9004C0 10.4473 0.00585938 8.40234 1.44727 6.9668L3.12305 5.29102C3.0293 5.74805 3.08789 6.26367 3.26953 6.69141L2.26758 7.69922C1.27734 8.68359 1.27148 10.084 2.27344 11.0801C3.26367 12.0703 4.6582 12.0703 5.64844 11.0801L7.59375 9.13477C8.58398 8.15039 8.58398 6.75 7.59375 5.75391C7.23047 5.39648 6.73242 5.16797 6.03516 5.10352L6.92578 4.20703Z" fill={color}/>
  </svg>
)

/* Fetch OG metadata from our own /api/og endpoint.
 * Returns { title, image } on any usable result, or null on total failure
 * (network down, JSON parse error in local dev, timeout, etc).
 *
 * The AbortController gives us a hard timeout: on weak wifi the request
 * could otherwise hang indefinitely and the card would sit on
 * "Loading preview…" forever. */
async function fetchOg(url) {
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), CONFIG.fetchTimeoutMs)
  try {
    const res = await fetch(`/api/og?url=${encodeURIComponent(url)}`, { signal: controller.signal })
    if (!res.ok) return null
    const data = await res.json()
    if (!data) return null
    // The endpoint may return an error with empty fields — only useful
    // if there's a title or image to show
    if (!data.title && !data.image) return null
    return { title: data.title || '', image: data.image || null }
  } catch {
    return null
  } finally {
    clearTimeout(timer)
  }
}

function LinkPreviewCard({ url }) {
  // Initialize state from cache — no loading flash for known URLs
  const [preview, setPreview] = useState(() => {
    const cached = previewCache.get(url)
    if (cached) return { ...cached, loading: false }
    return { title: '', image: null, loading: true }
  })
  // Track whether the preview image itself failed to load (broken/slow URL),
  // so we can fall back to the placeholder instead of a broken image icon.
  const [imgFailed, setImgFailed] = useState(false)

  useEffect(() => {
    let cancelled = false
    setImgFailed(false)

    // Cache hit — done
    if (previewCache.has(url)) {
      const c = previewCache.get(url)
      setPreview({ title: c.title, image: c.image, loading: false })
      return
    }

    // Another card is already fetching this URL — wait for it
    if (inflight.has(url)) {
      inflight.get(url).then(result => {
        if (cancelled) return
        if (result) setPreview({ ...result, loading: false })
        else setPreview({ title: '', image: null, loading: false })
      })
      return
    }

    // Kick off fetch and register so siblings dedupe
    const promise = fetchOg(url)
      .then(result => {
        if (result) previewCache.set(url, result)
        return result
      })
      .finally(() => {
        inflight.delete(url)
      })

    inflight.set(url, promise)

    promise.then(result => {
      if (cancelled) return
      if (result) setPreview({ ...result, loading: false })
      else setPreview({ title: '', image: null, loading: false })
    })

    return () => { cancelled = true }
  }, [url])

  // Trim long URLs for display
  const displayUrl = (() => {
    try {
      const u = new URL(url)
      const path = u.pathname.length > CONFIG.urlPathMaxLength
        ? u.pathname.slice(0, CONFIG.urlPathMaxLength) + '…'
        : u.pathname
      return u.hostname + path
    } catch { return url.slice(0, CONFIG.urlFallbackLength) }
  })()

  const showImage = preview.image && !imgFailed

  return (
    <a href={url} target="_blank" rel="noopener noreferrer" style={{ display: 'block', textDecoration: 'none', marginTop: CONFIG.cardMarginTop, maxWidth: CONFIG.cardMaxWidth }}>
      <div style={{ background: CONFIG.cardBackground, borderRadius: CONFIG.cardBorderRadius, overflow: 'hidden', border: CONFIG.cardBorder }}>
        {showImage ? (
          <img
            src={preview.image}
            alt=""
            loading="lazy"
            onError={() => setImgFailed(true)}
            style={{ width: '100%', height: CONFIG.previewHeight, objectFit: CONFIG.previewObjectFit, display: 'block' }}
          />
        ) : (
          <div style={{ width: '100%', height: CONFIG.placeholderHeight, background: CONFIG.placeholderBg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <SFLinkIcon size={CONFIG.iconSize} color={CONFIG.iconColor} />
          </div>
        )}
        <div style={{ padding: CONFIG.textPadding }}>
          <p style={{ fontSize: CONFIG.urlFontSize, color: CONFIG.urlColor, margin: `0 0 ${CONFIG.urlMarginBottom}`, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {displayUrl}
          </p>
          {preview.title ? (
            <p style={{ fontSize: CONFIG.titleFontSize, color: CONFIG.titleColor, margin: 0, fontWeight: CONFIG.titleFontWeight, display: '-webkit-box', WebkitLineClamp: CONFIG.titleMaxLines, WebkitBoxOrient: 'vertical', overflow: 'hidden', lineHeight: CONFIG.titleLineHeight }}>
              {preview.title}
            </p>
          ) : preview.loading ? (
            <p style={{ fontSize: CONFIG.loadingFontSize, color: CONFIG.loadingColor, margin: 0 }}>
              Loading preview…
            </p>
          ) : null}
        </div>
      </div>
    </a>
  )
}

export default React.memo(LinkPreviewCard)